import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { DataTable, Select, Typography, useSelect } from '@chilecompra/react-kit/components'
import { formatAmount, formatPercentage } from '../../modules/utils/formatters'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import generateYearsBetween from '../../modules/utils/years'
import { onBiddingsDistributionThunk } from './BiddingsDistribution.actions'
import { onMainIndicatorsThunk } from '../MainIndicators/MainIndicators.actions'
import MainIndicators from '../MainIndicators/MainIndicators.container'
import useSankeyChart from '../../components/Charts/SankeyChart.hooks'
import IconButton from '../../components/Buttons/IconButton'
import Infobar from '../../components/Infobar/Infobar'
import SankeyChart from '../../components/Charts/SankeyChart'
import BiddingsDistributionNoInfo from '../../components/Errors/BiddingsDistributionNoInfo'
import FichaBiddingsDistribution from '../../components/Infographics/FichaBiddingsDistribution'
import { Exportable, Wrapper, GridWrapper, Link } from './BiddingsDistribution.styles'

/**
 * The BiddingsDistribution's container.
 */
const BiddingsDistribution = () => {
  const dispatch = useDispatch()
  const { latestUpdate } = useSelector(state => state.topNavbar)
  const { idEntity } = useSelector(state => state.buyerSection)
  const inputSankeyChartRef = useRef()
  const mainIndicatorsRef = useRef()
  const dataTableRef = useRef()
  const [showTable, setShowTable] = useState(null)
  const [expandArrow, setExpandArrow] = useState(false)
  const [fileName, setFileName] = useState('Distribución_Licitaciones')
  const { endYear, loading: initialLoading, startYear } = useSelector(state => state.topNavbar)
  const {
    data: { chartStructure, csvStructure: csvBiddingsStructure },
    fetching: loading
  } = useSelector(state => state.biddingsDistribution)
  const years = generateYearsBetween(endYear, startYear)
  const { value: yearsValue, onChange: handleChange } = useSelect({
    initialValue: years[0].value,
    changeCallback: updatedValue => {
      dataTableRef.current?.setPage(1)
      dispatch(onBiddingsDistributionThunk(updatedValue, idEntity, distValue))
      dispatch(onMainIndicatorsThunk(updatedValue, idEntity))
      setFileName(`Distribución_Licitaciones_${updatedValue}`)
    }
  })
  const { value: distValue, onChange: handleDistribution } = useSelect({
    initialValue: 1,
    changeCallback: updatedValue => {
      dispatch(onBiddingsDistributionThunk(yearsValue, idEntity, updatedValue))
    }
  })
  const {
    data: { licMin, licMas, licDes },
    details: { csvStructure: csvIndicatorsStructure }
  } = useSelector(state => state.mainIndicators)
  const { serie: info } = useSankeyChart(chartStructure, distValue)
  const distribution = [
    { name: 'Montos transados', value: 1 },
    { name: 'N° de licitaciones adjudicadas', value: 2 },
    { name: 'N° de órdenes de compra', value: 3 }
  ]
  const headersByDistribution = {
    1: 'Montos transados',
    2: 'N° de licitaciones adjudicadas',
    3: 'N° de órdenes de compra'
  }
  const handleOpenIndicatorsDetails = updatedValue => {
    if (updatedValue === 'indicators') {
      setFileName(`Distribución_Licitaciones_Indicadores_${yearsValue}`)
    }

    setShowTable(updatedValue)
  }
  const handleShowTable = updatedValue => () => {
    if (updatedValue === 'details') {
      mainIndicatorsRef.current.close()
      setFileName(`Distribución_Licitaciones_${yearsValue}`)
    }

    setShowTable(updatedValue)
  }

  const handleCaptureImage = () => exportToImage('BiddingsDistributionImage', fileName)
  const handleDownloadFile = () =>
    exportToCsv(showTable === 'indicators' ? csvIndicatorsStructure : csvBiddingsStructure, fileName)

  useEffect(() => {
    if (!idEntity || initialLoading) {
      return
    }

    dispatch(onBiddingsDistributionThunk(yearsValue, idEntity, distValue))
    dispatch(onMainIndicatorsThunk(yearsValue, idEntity))
  }, [initialLoading, idEntity])

  return (
    <Wrapper>
      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12} md={10}>
          <Typography variant="h2" fontSize="22px" fontWeight="bold">
            Distribución de sus licitaciones
          </Typography>
          <Typography variant="body2" fontSize="16px" lineHeight="20px" margin="16px 0 48px">
            Las licitaciones son un procedimiento administrativo efectuado en forma autónoma por cada organismo
            comprador. Se publica una invitación abierta a través de Mercado Público a los proveedores que puedan
            proporcionar un bien o servicio. Posteriormente se selecciona y adjudica la oferta más conveniente, según
            los criterios que se establezcan en las bases de licitación
            <br />
            <br />A continuación podrás conocer cómo se distribuyen los montos transados, el número de órdenes de compra
            y el número de licitaciones distribuidas según sus montos (L1, LE, LP, LQ, entre otros). Recuerda que estos
            montos que se extraen de la orden de compra que emite una entidad en{' '}
            <Link href="https://www.mercadopublico.cl/Home">www.mercadopublico.cl</Link>, corresponden a compromisos de
            los organismos, no a pagos.
            <br />
            <br />
            La información referente a las licitaciones se basa en todas aquellas que se encuentren en estado
            “Adjudicada” o “Re-adjudicada”, y la información de montos transados dentro de esta sección corresponde al
            monto bruto (monto neto + impuestos).
          </Typography>
        </GridWrapper>
      </GridWrapper>
      <GridWrapper container alignItems="center" justifyContent="center">
        <GridWrapper container item align="left" md={8} spacing={1} xs={12}>
          <GridWrapper item align="left" md={3} xs={12}>
            <Select label="AÑO" options={years} value={yearsValue} onChange={handleChange} />
          </GridWrapper>
          <GridWrapper item align="left" md={5} xs={12}>
            <Select label="DISTRIBUCIÓN" options={distribution} value={distValue} onChange={handleDistribution} />
          </GridWrapper>
        </GridWrapper>
        <GridWrapper item xs={12} md={2} align="right" margin="6px 0 0">
          <IconButton
            singleIcon
            disabled={showTable !== 'details'}
            margin="25px 10px 0 0"
            type="toggleChart"
            variant="outlined"
            onClick={handleShowTable(null)}
          />
          <IconButton
            singleIcon
            disabled={showTable === 'details'}
            margin="25px 0 0"
            type="toggleTable"
            variant="outlined"
            onClick={handleShowTable('details')}
          />
        </GridWrapper>
        {showTable === 'details' ? (
          <GridWrapper item xs={12} md={10}>
            <DataTable
              headers={[
                { label: 'Año', value: 0 },
                { label: 'Nombre', value: 1 },
                { label: headersByDistribution[distValue], value: 2 },
                { label: 'Monto porcentual', value: 3 }
              ]}
              headersCellsProps={[
                null,
                { enableFilter: true, enableSort: true },
                { enableSort: true },
                { enableSort: true }
              ]}
              initialRows={6}
              loading={loading}
              records={csvBiddingsStructure.slice(1)}
              recordsDecorators={[
                ([year]) => year,
                ([, name]) => name,
                ([, , value]) => (distValue === 1 || distValue === 4 ? `$${formatAmount(value)}` : value),
                ([, , , percentage]) => formatPercentage(percentage)
              ]}
              ref={dataTableRef}
            />
          </GridWrapper>
        ) : (
          <Exportable id="BiddingsDistributionImage" padding="16px 0 16px 0">
            <GridWrapper item xs={12} md={10}>
              {chartStructure.length >= 1 ? (
                <SankeyChart
                  inputSankeyChartRef={inputSankeyChartRef}
                  data={info}
                  id="BiddingsDistribution"
                  fileName={fileName}
                />
              ) : (
                <BiddingsDistributionNoInfo />
              )}
            </GridWrapper>
          </Exportable>
        )}
      </GridWrapper>
      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12} md={10}>
          <MainIndicators
            idEntity={idEntity}
            licDes={licDes}
            licMas={licMas}
            licMin={licMin}
            ref={mainIndicatorsRef}
            year={yearsValue}
            onOpen={handleOpenIndicatorsDetails}
          />
        </GridWrapper>
      </GridWrapper>
      <GridWrapper container justifyContent="center" margin="32px 0 0">
        <GridWrapper item xs={12} md={10}>
          <Infobar
            disableDownloadChart={!!showTable}
            disableDownloadCsv={!showTable}
            disableInfographic={showTable}
            expandArrow={expandArrow}
            infographic={<FichaBiddingsDistribution />}
            latestUpdate={latestUpdate}
            onCaptureImage={handleCaptureImage}
            onDownloadFile={handleDownloadFile}
            setExpandArrow={setExpandArrow}
          />
        </GridWrapper>
      </GridWrapper>
    </Wrapper>
  )
}

export default BiddingsDistribution
